import {
  Box,
  Button,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Switch,
  Tooltip,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { accent, font, fontSize, neutral, radius, shadow, text } from 'styles/revampTokens';
import { handleBuilderModeTab } from 'redux/BuilderModeSlice';
import {
  FetchBlockOutputs,
  getBaseScenario,
  getPlannerBlock,
  getPlannerModel,
  UpdateBlockDimension,
} from 'redux/PlannerModeSlice';
import { fetchBlock, FetchBlockItemDimensions } from 'redux/PlanPageSlice';

import { DimensionsPill } from './components/HeaderPills';

const PlannerModeBlockSetting = () => {
  const { blockId, modelId } = useParams();
  const dispatch: any = useDispatch();
  const baseScenario = useSelector(getBaseScenario);
  const plannerModel = useSelector(getPlannerModel);
  const plannerBlock = useSelector(getPlannerBlock);
  const navigate = useNavigate();

  const doesDimensionExist = (dim: any, dimArray: any[]) => {
    return dimArray?.some((data) => data?.id === dim?.id);
  };

  const fetchIndicatorsData = (dimensions: any) => {
    const idArray = dimensions;
    const array2Ids = plannerBlock?.dimensions.find((object: any) => object.name === 'Time');
    const arrayIndex = idArray?.indexOf(array2Ids?.id);
    if (arrayIndex !== -1) {
      idArray.splice(arrayIndex, 1);
    }
    const dimensionIds = idArray.join(', ');
    const payload = {
      blockId,
      params: {
        dim_id: dimensionIds,
        indicator_filter: 'all',
        scenario_id: baseScenario?.id,
      },
    };
    dispatch(FetchBlockOutputs(payload));
  };

  const handleSettingChange = async (data: any, event: any) => {
    const dimensionsList = plannerBlock?.dimensions.filter((item: any) => item.name !== 'Time');
    let dimensions: any[] = plannerBlock?.dimensions || [];
    const dataIndex = dimensions.findIndex((item) => item?.id === data?.id);
    dimensions =
      dataIndex !== -1
        ? dimensions.filter((item, index) => index !== dataIndex)
        : [...dimensions, data];
    dimensions = dimensions.map((item) => item.id);
    await dispatch(
      UpdateBlockDimension({
        blockId,
        request_body: { dimension_id: dimensions },
        toaster: true,
      }),
    ).then((response: any) => {
      if (!response?.error) {
        fetchIndicatorsData(dimensions);
        dispatch(fetchBlock(Number(blockId)));
      }
    });
    await dispatch(
      FetchBlockItemDimensions({
        blockId,
      }),
    );
    if ((event.target.checked && dimensionsList?.length === 0) || !event.target.checked) {
      let dimension: any[] = plannerBlock?.dimensions || [];
      const dataIndexes = dimension.findIndex((item) => item?.id === data?.id);
      dimension =
        dataIndexes !== -1
          ? dimension.filter((item, index) => index !== dataIndexes)
          : [...dimension, data];
      dimension = dimension.map((item) => item.id);
      dispatch(UpdateBlockDimension({ blockId, request_body: { dimension_id: dimension } })).then(
        (response: any) => {
          if (!response?.error) {
            fetchIndicatorsData(dimension);
          }
        },
      );
    }
  };

  const renderMenuList = (list: any) => {
    const timeObject = list?.find((item: any) => item?.name === 'Time');
    const remainingList = list?.filter(
      (item: any) => !plannerBlock?.dimensions.some((element: any) => element?.id === item?.id),
    );
    const dimensionsList = plannerBlock?.dimensions?.filter((item: any) => item?.name !== 'Time');
    const filteredList = [
      ...(timeObject ? [timeObject] : []),
      ...(dimensionsList || []),
      ...(remainingList || []),
    ];
    return (
      <Box
        maxH='224px'
        overflowY='auto'
        pr='2px'
        // A thin neutral thumb instead of the app-wide 4px violet scrollbar,
        // which read as a thick accent-coloured bar in a small 240px panel.
        sx={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: neutral.border,
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': { background: neutral.borderStrong },
        }}
      >
        <Flex direction='column' gap='0px'>
          {filteredList?.map((data: any, index: number) => (
            <MenuItem
              key={index}
              as='li'
              h='32px'
              px='10px'
              // Label on the left, switch pinned to the right end.
              justifyContent='space-between'
              borderRadius={radius.control}
              fontFamily={font.body}
              fontSize={fontSize.body}
              fontWeight={500}
              color={text.primary}
              bg='transparent'
              _hover={{ bg: neutral.surfaceHover }}
              _focus={{ bg: neutral.surfaceHover }}
            >
              <Box as='span'>{data?.name}</Box>
              <Switch
                size='sm'
                // Chakra's default switch is blue; the checked track follows the
                // revamp accent so it matches the rest of the chrome. Chakra also
                // fades disabled controls to 40% opacity by default, which made
                // Time's off-state track nearly invisible against a white panel
                // — pinned back to full opacity with its own dedicated colour.
                sx={{
                  '.chakra-switch__track[data-checked]': { background: accent.solid },
                  '.chakra-switch__track[data-checked][data-disabled]': {
                    background: accent.subtle,
                  },
                  '.chakra-switch__track[data-disabled]:not([data-checked])': {
                    background: neutral.border,
                  },
                }}
                _disabled={{ opacity: 1 }}
                disabled={data?.name === 'time' || data?.name === 'Time'}
                isChecked={doesDimensionExist(data, plannerBlock?.dimensions)}
                onChange={(event: any) => handleSettingChange(data, event)}
              ></Switch>
            </MenuItem>
          ))}
        </Flex>
      </Box>
    );
  };

  const redirectToDimension = () => {
    navigate(`/model/${modelId}/home`);
    dispatch(handleBuilderModeTab(1));
  };

  return (
    <Flex alignItems={'center'}>
      {/* autoSelect is what auto-highlights the first item on open — it's what
          gave the disabled Time row a permanent focus-grey wash. */}
      <Menu closeOnSelect={false} variant={'primary'} autoSelect={false}>
        <Tooltip label='Block Dimensions'>
          {/* The header's revamp pill as the trigger — this component's own
              button was the old icon-and-count one. */}
          <MenuButton
            as={DimensionsPill}
            selectedDimensionCount={
              plannerBlock?.dimensions?.filter((item: any) => item?.name !== 'Time')?.length ?? 0
            }
          />
        </Tooltip>
        <MenuList
          w='264px'
          pb='8px'
          pt='10px'
          px='8px'
          maxH='320px'
          zIndex={10}
          bg={neutral.surface}
          border='1px solid'
          borderColor={neutral.border}
          borderRadius={radius.surface}
          boxShadow={shadow.panel}
        >
          <Box
            px='8px'
            pb='8px'
            fontFamily={font.body}
            fontSize={fontSize.label}
            fontWeight={600}
            color={text.secondary}
            textTransform='uppercase'
            letterSpacing='0.04em'
          >
            Dimensions
          </Box>
          {renderMenuList(plannerModel?.dimensions)}
          <Divider borderColor={neutral.borderSubtle} my='8px' />
          <Button
            variant='unstyled'
            w='100%'
            h='36px'
            px='8px'
            mt='-8px'
            display='flex'
            justifyContent='flex-start'
            fontFamily={font.body}
            fontSize={fontSize.body}
            fontWeight={600}
            color={accent.solid}
            borderRadius={radius.control}
            _hover={{ bg: neutral.surfaceHover }}
            onClick={() => redirectToDimension()}
          >
            Edit dimensions
          </Button>
        </MenuList>
      </Menu>
    </Flex>
  );
};

export default PlannerModeBlockSetting;
